import {
  Alert,
  Container,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import * as api from '../../services/api';
import { User } from '../../types';
import SortableTable, { HeadCell } from '../common/SortableTable';

const ROLE_OPTIONS: User['userType'][] = ['admin', 'labeller', 'trainee'];

const UsersManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [updating, setUpdating] = useState<Record<string, boolean>>({});
  const { notification, setNotification, hideNotification } = useNotification();

  useEffect(() => {
    Promise.all([api.fetchUsers(), api.getCurrentUser()])
      .then(([userData, me]) => {
        setUsers(userData);
        setCurrentUser(me);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
      });
  }, []);

  const handleRoleChange = async (user: User, newRole: User['userType']) => {
    if (newRole === user.userType) return;
    setUpdating(prev => ({ ...prev, [user.id]: true }));
    try {
      const updated = await api.updateUserRole(user.id, newRole);
      setUsers(prev => prev.map(u => (u.id === user.id ? updated : u)));
      setNotification({
        open: true,
        message: `Updated ${user.email} to ${updated.userType}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      setNotification({
        open: true,
        message: 'Failed to update user role',
        severity: 'error',
      });
    } finally {
      setUpdating(prev => {
        const next = { ...prev };
        delete next[user.id];
        return next;
      });
    }
  };

  const headCells: HeadCell<User>[] = [
    {
      id: 'firstName',
      label: 'First Name',
      numeric: false,
      sortable: true,
    },
    {
      id: 'lastName',
      label: 'Last Name',
      numeric: false,
      sortable: true,
    },
    {
      id: 'email',
      label: 'Email',
      numeric: false,
      sortable: true,
    },
    {
      id: 'userType',
      label: 'User Type',
      numeric: false,
      sortable: true,
      renderCell: user => {
        const isSelf = currentUser?.id === user.id;
        return (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={user.userType}
              disabled={updating[user.id] || isSelf}
              onChange={e => handleRoleChange(user, e.target.value as User['userType'])}
            >
              {ROLE_OPTIONS.map(role => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Typography variant="h4" component="h1">
          Users Management
        </Typography>
      </div>

      <SortableTable
        data={users}
        headCells={headCells}
        defaultSortBy="lastName"
        defaultOrder="asc"
        idAccessor={user => user.id}
        emptyMessage="No users found"
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersManagement;
